import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert 
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const GuestQRVerify = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const tenantId = queryParams.get('tenantId');

  useEffect(() => {
    // If no token or tenantId is present in URL, display an error
    if (!token || !tenantId) {
      setError('Invalid QR Code. Please scan the QR code provided in your room again.');
    }
  }, [token, tenantId]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!phoneNumber) {
      setError('Please enter your registered phone number.');
      return;
    }
    if (phoneNumber.length < 5) {
      setError('Please enter a valid phone number.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL_PMS || 'http://localhost:3000';
      const response = await axios.post(
        `${baseUrl}/api/v1/public/guest/verify`,
        {
          roomToken: token,
          phoneNumber: phoneNumber,
        },
        {
          headers: {
            'x-tenant-id': tenantId,
          },
        }
      );

      if (response.data?.status === 'success') {
        const guestId = response.data.data.guestId;
        const roomId = response.data.data.roomId;
        // Optionally save the guestName and roomNumber to localStorage here for personalized UI later
        localStorage.setItem('pmsgo_guest_name', response.data.data.guestName);
        localStorage.setItem('pmsgo_room_number', response.data.data.roomNumber);

        // Redirect to the guest menu
        navigate(`/?guestId=${guestId}&roomId=${roomId}&tenantId=${tenantId}`);
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (err) {
      if (err.response?.status === 401) {
         setError('Details do not match our records. Please verify the phone number linked to your reservation.');
      } else if (err.response?.status === 429) {
         setError('Too many failed attempts. Please try again after 15 minutes.');
      } else if (err.response?.data?.message) {
         setError(err.response.data.message);
      } else {
         setError('An error occurred during verification. Please contact reception.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token || !tenantId) {
    return (
      <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {error || 'Invalid QR Code scanned.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8' }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
            Welcome to PMSGO!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please enter your registered mobile number to access the Room Service Menu.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleVerify}>
          <TextField
            fullWidth
            label="Mobile Number"
            variant="outlined"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 15))}
            placeholder="Registered mobile number"
            disabled={loading}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={loading || !phoneNumber.trim()}
            sx={{ py: 1.5, fontSize: '1.1rem', textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Enter'}
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default GuestQRVerify;
